import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты для сравнения. Сохраните хотя бы один расчет.
        </p>
      </div>
    );
  }

  // Типы судов и исков для отображения
  const courtTypeNames = {
    'arbitration': 'Арбитражный суд',
    'civil': 'Суд общей юрисдикции',
    'administrative': 'Административный суд',
    'commercial': 'Коммерческий арбитраж'
  };

  const claimTypeNames = {
    'property': 'Имущественный',
    'non_property': 'Неимущественный',
    'contract': 'Договорной',
    'administrative': 'Административный',
    'bankruptcy': 'Банкротство'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Сумма иска:</span>
                <span className="font-medium">
                  {formatCurrency(calculation.formData.claimAmount, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип суда:</span>
                <span>
                  {courtTypeNames[calculation.courtType] || calculation.courtType}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип иска:</span>
                <span>
                  {claimTypeNames[calculation.claimType] || calculation.claimType}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Госпошлина:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calculation.results.discountedCourtFee, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Итого расходы:</span>
                <span className="font-semibold text-rose-600 dark:text-rose-400">
                  {formatCurrency(calculation.results.totalCost, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSidebar;